import { useState } from 'react';

import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import BookmarkAddedRoundedIcon from '@mui/icons-material/BookmarkAddedRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ConstructionRoundedIcon from '@mui/icons-material/ConstructionRounded';
import DownloadIcon from '@mui/icons-material/Download';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import ViewStreamRoundedIcon from '@mui/icons-material/ViewStreamRounded';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import ReportSection from './ReportSection';
import { BUSINESS_COST_SECTIONS, CLICKSTREAM_COMMON_PROPS } from '../../../utils/constants';
import * as Styled from '../../BusinessCostResults.style';

/**
 * Main content panel displaying detailed cost report
 * Renders cost breakdown sections with estimates
 *
 * @param {object} props - Component props
 * @param {object} props.report - Cost breakdown report
 * @param {Function} props.onDownloadReport - Handler for download report action
 * @param {boolean} [props.loading=false] - Whether download is in progress
 * @component
 */
function ReportPanel({ report, onDownloadReport, loading = false }) {
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownloadReport = async () => {
    if (downloadLoading || loading) return;

    setDownloadLoading(true);
    if (onDownloadReport && report) {
      await onDownloadReport(report.id);
    }
    setDownloadLoading(false);
  };
  console.log('report', report);

  if (!report) {
    return (
      <Styled.MainContent>
        <Styled.ContentHeader>
          <Styled.ContentTitle color="secondary" size="sm">
            No cost estimate available
          </Styled.ContentTitle>
        </Styled.ContentHeader>
      </Styled.MainContent>
    );
  }

  // Icon mapping for cost sections
  const iconMap = {
    annualReport: <AssignmentRoundedIcon />,
    bookkeepingTools: <BookmarkAddedRoundedIcon />,
    formation: <GridViewRoundedIcon />,
    generalOperatingCosts: <PaymentsRoundedIcon />,
    licensesPermits: <BadgeRoundedIcon />,
    officeOrPhysicalLocation: <HomeWorkRoundedIcon />,
    operatingEquipment: <ConstructionRoundedIcon />,
    registeredAgent: <PersonAddAltRoundedIcon />,
    summary: null,
    taxes: <AttachMoneyRoundedIcon />,
    estimatedTotal: <ViewStreamRoundedIcon />,
    websiteMarketingExpenses: <CampaignRoundedIcon />
  };

  // Get icon for section ID
  // const getIconForSection = (sectionId) => {
  //   const normalizedId = sectionId?.toLowerCase().replace(/\s+/g, '_').replace(/&/g, '');
  //   return iconMap[normalizedId] || <DescriptionIcon />;
  // };

  // Get icon for section ID
  const getIconForSection = (sectionId) => {
    if (iconMap[sectionId] === null) return null;
    return iconMap[sectionId] || iconMap.default;
  };

  // Helper function to format section titles from camelCase or snake_case keys
  const formatSectionTitle = (key) => {
    return key
      .replace(/_/g, ' ') // Replace underscores with spaces (for snake_case)
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters (for camelCase)
      .replace(/&/g, 'and') // Replace & with 'and'
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  // Build report sections from cost breakdown
  const sections = Object.entries(report.sections || {})
    .filter(([, value]) => value)
    .map(([key, value]) => ({
      id: key,
      title: formatSectionTitle(key),
      icon: getIconForSection(key),
      cost: value?.cost,
      content: (typeof value === 'string' ? value : undefined) || value?.content
    }));

  return (
    <Styled.MainContent>
      <Styled.ContentHeader>
        <Text color="secondary" size="sm">
          Detailed report
        </Text>

        <Styled.ContentSubHeader>
          <Box>
            <Text color="primary" mb={1} size="xl" weight="medium">
              {report.title}
            </Text>
            <Text color="secondary" size="md">
              {report.description}
            </Text>
          </Box>
          <Styled.DownloadButton
            clickstreamProps={{
              ...CLICKSTREAM_COMMON_PROPS,
              elementName: 'business_cost_download',
              elementText: 'Business Cost Download'
            }}
            color="primary"
            disabled={downloadLoading || loading}
            loading={downloadLoading}
            size="small"
            startIcon={<DownloadIcon />}
            variant="contained"
            onClick={handleDownloadReport}
          >
            Download report
          </Styled.DownloadButton>
        </Styled.ContentSubHeader>
      </Styled.ContentHeader>

      <Styled.ContentBody>
        {/* Cost Breakdown Sections */}
        {BUSINESS_COST_SECTIONS.map((section) => {
          const sectionContent = sections.find((s) => s.id === section.id);
          if (!sectionContent) return null;
          return (
            <ReportSection
              key={sectionContent.id}
              content={sectionContent.content}
              costRange={sectionContent.cost}
              disclaimer={section.disclaimer}
              icon={sectionContent.icon}
              isBubble={section.isBubble}
              showDisclaimer={section.showDisclaimer}
              title={sectionContent.title}
            />
          );
        })}

        <Styled.FooterSectionContent>
          <Styled.DownloadButton
            clickstreamProps={{
              ...CLICKSTREAM_COMMON_PROPS,
              elementName: 'business_cost_download',
              elementText: 'Business Cost Report Download'
            }}
            color="primary"
            disabled={downloadLoading || loading}
            loading={downloadLoading}
            size="small"
            startIcon={<DownloadIcon />}
            variant="contained"
            onClick={handleDownloadReport}
          >
            Download report
          </Styled.DownloadButton>
        </Styled.FooterSectionContent>
      </Styled.ContentBody>
    </Styled.MainContent>
  );
}

ReportPanel.propTypes = {
  report: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    sections: PropTypes.object.isRequired
  }),
  onDownloadReport: PropTypes.func,
  loading: PropTypes.bool
};

export default ReportPanel;
